import { Component, EventEmitter, OnInit } from "@angular/core";
import { NgxSpinnerService } from "ngx-spinner";
import { SelectItem } from "primeng/api";
import { NotificationTypes } from "src/app/app.enums";
import { ArtFlowSelectItem } from "src/app/models/artflow.selectitem";
import { EmailEventTypeService } from "src/app/Services/email-event-type.service";
import { NotificationsService } from "src/app/Services/shared/notifications.service";
import { copyToClipboard } from 'src/app/app.functions';
import { Events, Parameters, SaveTemplates } from "src/app/models/emailevents.model";
import { SmsTemplatePayload, SmsTemplateService } from "src/app/Services/sms-template-service/sms-template.service";
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'sms-setting-template-dailog',
    templateUrl: './sms-setting-template-dailog.component.html',
    inputs: ['displayDialog', 'isEditMode', 'emailEvents', 'selectedSmsTemplate', 'emailParameters'],
    outputs: ['onCloseEmailTemplateDailog', 'fetchEmailTemplate'],
    standalone: false
})
export class SMSSettingTemplateDailogComponent implements OnInit {
  public isEditMode: boolean = false;
  public displayDialog: boolean = false;
  public smsPreviewVisible: boolean = false;
  public loadedEmailTemplateParameters: boolean = false;
  public isAddSMSEventDailogVisible: boolean = false;
  public selectedSmsTemplate: SaveTemplates = {
    Guid: '',
    HtmlText: '',
    EmailEventId: '',
    Subject: ''
  };
  public emailEventsFormData!: Events;
  public selectedEmailEvent?: ArtFlowSelectItem;
  public emailEvents: Events[] = [];
  public emailParametersForAddEvents: SelectItem[] = [];
  public emailParameters: Parameters[] = [];
  public selectedEmailParameter: string | null = null;
  public testPhoneNumber: string = '';
  public onCloseEmailTemplateDailog = new EventEmitter<boolean>();
  public fetchEmailTemplate = new EventEmitter<void>();
  public isPredefined: boolean = false;

  constructor(
    private ngxSpinnerService: NgxSpinnerService,
    private notificationsService: NotificationsService,
    private emailEventTypeService: EmailEventTypeService,
    private smsTemplateService: SmsTemplateService,
    private transloco: TranslocoService
  ) {
    this.emailEventsFormData = new Events();
  }

  ngOnInit(): void {
    if (this.isEditMode) {
      this.fetchEmailEvents();
    }
  }

  public onCloseDialog() {
    this.displayDialog = false;
    this.isEditMode = false;
    this.selectedSmsTemplate = new SaveTemplates(); // Reset the form data
    this.selectedEmailEvent = undefined; // Reset the selected event
    this.emailParameters = []; // Clear parameters
    this.selectedEmailParameter = null; // Reset selected parameter
    this.onCloseEmailTemplateDailog.emit(false); // Emit false to close dialog in parent
  }

  public onAddSMSEvents() {
    this.isAddSMSEventDailogVisible = true;
    this.getEmailParameters();
  }

  public onCloseSMSEventsDialog($event: boolean) {
    this.emailEventsFormData = new Events();
    this.isAddSMSEventDailogVisible = $event;
  }

  public showSMSPreviewDialog() {
    this.smsPreviewVisible = true;
  }

  public fetchEmailEvents(): void {
    this.ngxSpinnerService.show();
    this.emailEventTypeService.fetchEmailevents(0).subscribe({
      next: (emailEvents: Events[]) => {
        this.emailEvents = emailEvents.map(evt => ({
          Id: evt.Id,
          Name: evt.Name,
          Description: evt.Description,
          IsPredefined: evt.IsPredefined,
          CreatedDate: evt.CreatedDate,
          EmailParameterIds: evt.EmailParameterIds,
          label: evt.Name,
          value: evt.Id
        }));
        this.ngxSpinnerService.hide();
      },
      error: (error) => {
        console.error('Error fetching SMS events:', error);
        this.notificationsService.showNotification(this.transloco.translate('smsSettingTemplateDailog.errorTitle'), this.transloco.translate('smsSettingTemplateDailog.failedToFetchSmsEvents'), NotificationTypes.Error);
        this.ngxSpinnerService.hide();
      }
    });
  }

  public getEmailParameters() {
    this.emailEventTypeService.fetchEmailParameters().subscribe((emailParameters) => {
      this.emailParametersForAddEvents = emailParameters.map((item) => ({
        label: item.ParameterName,
        value: item.Guid
      }));
    }, (error) => {
      console.error(error);
    });
  }

  public onEmailEventChanged($event: any) {
    const eventId = $event.value;
    this.getEmailTemplateParameters(eventId);
  }

  private getEmailTemplateParameters(eventId: string) {
    this.loadedEmailTemplateParameters = false;
    this.emailEventTypeService.fetchEmailParameters(eventId).subscribe((emailEvents: Parameters[]) => {
      this.emailParameters = emailEvents;
      this.loadedEmailTemplateParameters = true;
    }, (error) => {
      console.error(error);
    });
  }

  public onEmailParameterChanged($event: any) {
    this.selectedEmailParameter = $event.value;
  }

  public insertSMSParameter($event: any) {
    const selectedParameter = this.emailParameters.find(param => param.Guid === this.selectedEmailParameter);
    if (selectedParameter) {
      const currentValue = this.selectedSmsTemplate.HtmlText || '';
      this.selectedSmsTemplate.HtmlText = currentValue + `{{${selectedParameter.ParameterName}}}`;
      this.selectedEmailParameter = null;
    }
  }

  public copySelectedSMSParameter($event: any) {
    const selectedParameter = this.emailParameters.find(param => param.Guid === this.selectedEmailParameter);
    if (selectedParameter && selectedParameter.ParameterName) {
      const appendCurlyBrackets = `{{${selectedParameter.ParameterName}}}`;
      copyToClipboard(appendCurlyBrackets);
      this.notificationsService.showNotification(this.transloco.translate('smsSettingTemplateDailog.successTitle'), this.transloco.translate('smsSettingTemplateDailog.parameterCopiedToClipboard'), NotificationTypes.Success);
    }
  }

  public copySelectedSMSParameters($event: Parameters) {
    if ($event.ParameterName) {
      const appendCurlyBrackets = `{{${$event.ParameterName}}}`;
      copyToClipboard(appendCurlyBrackets);
      this.notificationsService.showNotification(this.transloco.translate('smsSettingTemplateDailog.successTitle'), this.transloco.translate('smsSettingTemplateDailog.parameterCopiedToClipboard'), NotificationTypes.Success);
    }
  }

  public saveSMSTemplate($event: any) {
    const smsTemplatePayload: SmsTemplatePayload = {
      id: this.selectedSmsTemplate.Guid || '',
      systemEventId: this.selectedSmsTemplate.EmailEventId,
      text: this.selectedSmsTemplate.HtmlText || '',
      createdDate: new Date().toISOString(),
      modifiedDate: new Date().toISOString(),
      eventTypeViewModel: {
        id: this.selectedSmsTemplate.EmailEventId,
        name: this.emailEvents.find(e => e.Id === this.selectedSmsTemplate.EmailEventId)?.Name || '',
        description: this.emailEvents.find(e => e.Id === this.selectedSmsTemplate.EmailEventId)?.Description || '',
        isPredefined: this.isEditMode ? this.isPredefined : false,
        isEmailEvent: false,
        createdDate: new Date().toISOString(),
        emailParameterIds: []
      }
    };
    this.ngxSpinnerService.show();
    this.smsTemplateService.saveSmsTemplate(smsTemplatePayload).subscribe(
      (response) => {
        this.ngxSpinnerService.hide();
        if (response.Success) {
          this.notificationsService.showNotification(this.transloco.translate('smsSettingTemplateDailog.successTitle'), response.ResponseMessage, NotificationTypes.Success);
          // Close the dialog and reset the form
          this.onCloseDialog();
          // Emit event to notify parent to reload data
          this.fetchEmailTemplate.emit();
        } else {
          this.notificationsService.showNotification(this.transloco.translate('smsSettingTemplateDailog.errorTitle'), response.ResponseMessage, NotificationTypes.Error);
        }
      },
      (error) => {
        console.error(error);
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(this.transloco.translate('smsSettingTemplateDailog.errorTitle'), this.transloco.translate('smsSettingTemplateDailog.failedToSaveSmsTemplate'), NotificationTypes.Error);
      }
    );
  }

  public sendTestSMS($event: any) {
    const testData = {
      SendTo: this.testPhoneNumber,
      Text: this.selectedSmsTemplate.HtmlText
    };
    this.smsTemplateService.sendTestSMS(testData).subscribe((response) => {
      this.ngxSpinnerService.hide();
      if (response.Success) {
        this.testPhoneNumber = '';
        this.notificationsService.showNotification(this.transloco.translate('smsSettingTemplateDailog.successTitle'), response.ResponseMessage, NotificationTypes.Success);
      } else {
        this.notificationsService.showNotification(this.transloco.translate('smsSettingTemplateDailog.errorTitle'), response.ResponseMessage, NotificationTypes.Error);
      }
    }, (error) => {
      console.error(error);
      this.ngxSpinnerService.hide();
    });
  }
}